import React, { useState } from 'react';
import {
    Box,
    Button,
    CheckBox,
    Form,
    FormField,
    Heading,
    Layer,
    Notification,
    Tip,
} from 'grommet';
import { Close, Edit } from 'grommet-icons';
import { useRevalidator } from 'react-router-dom';
import PropTypes from 'prop-types';
import { CompanyService } from '@enrich/shared-lib';

export const EditCompanyButton = ({ company }) => {
    const [showModal, setShowModal] = useState(false);
    const [notification, setNotification] = useState(null);

    return (
        <>
            <Button
                secondary
                size="medium"
                label={
                    <span>
                        <Edit size="medium" color="enrich-blue" />
                    </span>
                }
                onClick={() => setShowModal(true)}
            />
            {showModal && (
                <EditCompanyModal
                    company={company}
                    setShowModal={setShowModal}
                    setNotification={setNotification}
                />
            )}
            {notification && (
                <Notification
                    toast
                    status={notification.status}
                    title={notification.title}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
        </>
    );
};

const EditCompanyModal = ({ company, setShowModal, setNotification }) => {
    const [value, setValue] = useState({
        isReportClient: !!company.attributes.isReportClient,
        hideRaceData: !!company.attributes.hideRaceData,
    });
    const [buttonBusy, setButtonBusy] = useState(false);
    const revalidator = useRevalidator();

    const handleSubmit = async (submitted) => {
        setButtonBusy(true);
        try {
            await new CompanyService().update(company.id, {
                isReportClient: submitted.isReportClient,
                hideRaceData: submitted.hideRaceData,
            });
            setNotification({
                status: 'normal',
                title: 'Success',
                message: 'Company updated.',
            });
            setShowModal(false);
            revalidator.revalidate();
        } catch (error) {
            console.log(error);
            const message =
                error.response?.data?.error?.message ||
                error.message ||
                'Failed to update company.';
            setNotification({
                status: 'critical',
                title: 'Update failed',
                message,
            });
            setButtonBusy(false);
        }
    };

    const tipContent = value.isReportClient
        ? 'Turn off reporting'
        : 'Turn on reporting';

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                gap="30px"
                width="medium">
                <Box fill="horizontal" direction="row" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        Edit Company
                    </Heading>
                    <Box
                        align="end"
                        round={true}
                        onClick={() => setShowModal(false)}
                        cursor="pointer">
                        <Close color="enrich-red" />
                    </Box>
                </Box>
                <Box>
                    <Form
                        value={value}
                        onChange={(nextValue) => setValue(nextValue)}
                        onSubmit={({ value: submitted }) =>
                            handleSubmit(submitted)
                        }>
                        <FormField
                            label="Reporting Status"
                            name="isReportClient">
                            <Tip
                                content={tipContent}
                                dropProps={{
                                    align: { bottom: 'top' },
                                    margin: 'xsmall',
                                }}>
                                <Box pad={{ left: 'small', vertical: 'small' }}>
                                    <CheckBox
                                        toggle
                                        a11yTitle="Toggle reporting status"
                                        name="isReportClient"
                                        checked={!!value.isReportClient}
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                isReportClient:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </Box>
                            </Tip>
                        </FormField>
                        <FormField label="Hide Race Data" name="hideRaceData">
                            <Tip
                                content={
                                    value.hideRaceData
                                        ? 'Race data is hidden from this client'
                                        : 'Race data is visible to this client'
                                }
                                dropProps={{
                                    align: { bottom: 'top' },
                                    margin: 'xsmall',
                                }}>
                                <Box pad={{ left: 'small', vertical: 'small' }}>
                                    <CheckBox
                                        toggle
                                        a11yTitle="Toggle hide race data"
                                        name="hideRaceData"
                                        checked={!!value.hideRaceData}
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                hideRaceData:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </Box>
                            </Tip>
                        </FormField>
                        <Box>
                            <Button
                                primary
                                type="submit"
                                size="medium"
                                fill="horizontal"
                                busy={buttonBusy}
                                label="Submit"
                                alignSelf="center"
                            />
                        </Box>
                    </Form>
                </Box>
            </Box>
        </Layer>
    );
};

EditCompanyButton.propTypes = {
    company: PropTypes.object,
};

EditCompanyModal.propTypes = {
    company: PropTypes.object,
    setShowModal: PropTypes.func,
    setNotification: PropTypes.func,
};
